import type { RoleRef } from '@dawn/feature-config';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from '@dawn/ui';
import { useState } from 'react';

import { useInviteAdmin } from '@/hooks/mutations/use-invite-admin';
import { useUpdateAdminRoles } from '@/hooks/mutations/use-update-admin-roles';
import { useUpdateInvitationRoles } from '@/hooks/mutations/use-update-invitation-roles';
import { getActionErrorMessage } from '@/lib/admin-action-error';
import type { AdminListItem, InvitationListItem } from '@/lib/api';

type AdminRoleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant's roles from the roster, Super Admin → Admin → Analyst (TDEV-1213). */
  availableRoles: RoleRef[];
} & (
  | { mode: 'invite' }
  | { mode: 'edit-admin'; admin: AdminListItem }
  | { mode: 'edit-invitation'; invitation: InvitationListItem }
);

// Mirror the backend's @IsEmail check closely enough to catch obvious typos
// before the request; the server remains the source of truth.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The role popup. `invite`: invite a new user by email with one or more roles
 * (TDEV-141, TDEV-1213 step 09) via POST /admins/invite, which sends the
 * invitation and pre-provisions the user. `edit-admin`: replace an Admin's roles
 * (TDEV-1213 step 25) via PUT /admins/:id/roles. `edit-invitation`: replace the
 * roles a pending or expired invitation grants (step 26) via
 * PUT /admins/invitations/:id/roles. Both edit modes show the email read-only. Each
 * mutation invalidates the roster on success. Shows the standard loading /
 * success / error states inline.
 */
export function AdminRoleDialog(props: AdminRoleDialogProps) {
  const { open, onOpenChange, availableRoles } = props;
  // The row an edit mode works on: its email is shown read-only, its roles pre-checked.
  const editTarget =
    props.mode === 'edit-admin'
      ? props.admin
      : props.mode === 'edit-invitation'
        ? props.invitation
        : null;
  const [email, setEmail] = useState(editTarget?.email ?? '');
  // Invite: nothing pre-checked, the inviter makes a deliberate choice (TDEV-1213 New-1).
  // Edit: the row's current roles; the caller remounts the dialog per row.
  const [selected, setSelected] = useState<string[]>(
    () => editTarget?.roles.map((r) => r.id) ?? []
  );
  const [error, setError] = useState<string | null>(null);
  // Every hook every render (hook rules); the mode picks which one Save uses.
  const inviteAdmin = useInviteAdmin();
  const updateAdminRoles = useUpdateAdminRoles();
  const updateInvitationRoles = useUpdateInvitationRoles();
  const submitting = {
    invite: inviteAdmin,
    'edit-admin': updateAdminRoles,
    'edit-invitation': updateInvitationRoles,
  }[props.mode].isPending;
  const copy = editTarget
    ? {
        title: 'Edit roles',
        // An invitee has not joined yet, so "their next click" would be false (TDEV-1213 New-5).
        description:
          props.mode === 'edit-admin'
            ? `Choose the roles ${editTarget.email} holds. The change applies on their next click.`
            : `Choose the roles ${editTarget.email} will get when they accept the invitation.`,
        save: 'Save',
        saving: 'Saving…',
        failed: "Couldn't save the roles. Please try again.",
      }
    : {
        title: 'Invite a user',
        description: "They'll get an email invitation to join your organization's dashboard.",
        save: 'Send invitation',
        saving: 'Sending…',
        failed: "Couldn't send the invitation. Please try again.",
      };

  const reset = () => {
    setEmail('');
    setSelected([]);
    setError(null);
    inviteAdmin.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && submitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSave = async () => {
    const trimmed = email.trim();
    if (!trimmed || submitting) return;
    // Edit: the email is the row's and is never sent.
    if (!editTarget && !EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    if (selected.length === 0) {
      // The server's ROLE_REQUIRED wording, so both paths read the same.
      setError('Choose at least one role.');
      return;
    }
    setError(null);
    try {
      if (props.mode === 'edit-admin') {
        await updateAdminRoles.mutateAsync({ adminId: props.admin.id, roleIds: selected });
      } else if (props.mode === 'edit-invitation') {
        await updateInvitationRoles.mutateAsync({
          invitationId: props.invitation.id,
          roleIds: selected,
        });
      } else {
        await inviteAdmin.mutateAsync({ email: trimmed, roleIds: selected });
      }
      handleOpenChange(false);
    } catch (err) {
      // A 4xx reason (existing user, cap reached, Last Super Admin, invitation accepted)
      // is shown as the server words it.
      setError(getActionErrorMessage(err, copy.failed));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-5 rounded-xl bg-popover p-6 shadow-2xl sm:max-w-md sm:rounded-xl">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        {error && (
          <Banner variant="error" className="rounded-md border" data-testid="invite-admin-error">
            {error}
          </Banner>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="invite-admin-email">Email</Label>
          <Input
            id="invite-admin-email"
            type="email"
            placeholder="teammate@company.com"
            value={email}
            autoFocus={!editTarget}
            readOnly={editTarget !== null}
            disabled={submitting}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
            }}
            data-testid="invite-admin-email-input"
          />
        </div>

        <fieldset className="space-y-2" data-testid="invite-admin-roles">
          <legend className="mb-1 text-sm font-medium">Roles</legend>
          {availableRoles.map((role) => (
            <Checkbox
              key={role.id}
              className="flex"
              label={role.name}
              value={role.id}
              checked={selected.includes(role.id)}
              disabled={submitting}
              onChange={(e) =>
                setSelected((current) =>
                  e.target.checked ? [...current, role.id] : current.filter((id) => id !== role.id)
                )
              }
            />
          ))}
        </fieldset>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            data-testid="invite-admin-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!email.trim() || submitting}
            leadingIcon={submitting ? <Spinner animate size={16} /> : undefined}
            data-testid="invite-admin-submit"
          >
            {submitting ? copy.saving : copy.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
